import { createFileRoute } from "@tanstack/react-router";
import { lazy } from "react";
import { pageHead, publicRow, SITE_URL } from "@/lib/route-head";
const SpecialEventDetail = lazy(() => import("@/pages/SpecialEventDetail"));

type EventRow = {
  id: string;
  title: string | null;
  description: string | null;
  event_date: string | null;
  event_time: string | null;
  price_per_person: number | null;
  status: string | null;
  restaurants: { city: string | null; state: string | null } | null;
};

export const Route = createFileRoute("/experiences/$eventId")({
  component: SpecialEventDetail,
  loader: async ({ params }) => {
    const id = params.eventId;
    if (!/^[0-9a-f-]{36}$/i.test(id)) return { event: null };
    const event = await publicRow<EventRow>(
      `dinner_events?id=eq.${id}&select=title,description,event_date,event_time,price_per_person,status,restaurants(city,state)`
    );
    return { event };
  },
  head: ({ params, loaderData }) => {
    const event = loaderData?.event ?? null;
    if (!event || !event.event_date) {
      return pageHead({
        path: `/experiences/${params.eventId}`,
        title: "An Amorette Evening",
        description: "A curated Amorette dinner experience. Sign in to see details and reserve your seat.",
      });
    }
    const city = event.restaurants?.city ?? null;
    const state = event.restaurants?.state ?? null;
    const where = [city, state].filter(Boolean).join(", ");
    const title = event.title ?? "An Amorette Evening";
    const description = event.description?.slice(0, 160) ||
      `A curated Amorette dinner experience${where ? ` in ${where}` : ""}. Reserve your seat — venues are revealed 12 hours before the evening.`;
    const startIso = event.event_time ? `${event.event_date}T${event.event_time}` : event.event_date;
    const head = pageHead({ path: `/experiences/${params.eventId}`, title, description, type: "article" });
    return {
      ...head,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            name: title,
            description,
            startDate: startIso,
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            location: where
              ? { "@type": "Place", name: where, address: { "@type": "PostalAddress", addressLocality: city, addressRegion: state, addressCountry: "US" } }
              : undefined,
            organizer: { "@type": "Organization", name: "Amorette", url: SITE_URL },
            url: `${SITE_URL}/experiences/${params.eventId}`,
          }),
        },
      ],
    };
  },
});
